import React, { useEffect, useState } from 'react';
import { getPostMortem } from '../../services/OrdersService';
import { formatNumber } from '../../utils/NumberUtils';

const REGIME_LABELS = {
    TRENDING_UP: '📈 Alta',
    TRENDING_DOWN: '📉 Baixa',
    RANGING: '↔️ Lateral',
    HIGH_VOLATILITY: '⚡ Volátil'
};

function fmtDate(ts) {
    if (!ts) return '—';
    return new Date(parseInt(ts)).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function fmtDuration(ms) {
    if (!ms) return '—';
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m`;
    return `${s}s`;
}

/**
 * Modal de post-mortem de uma trade. Mostra contexto completo
 * (ordem + sinal IA + regime + automação + round-trip pareada) num
 * único painel — o "detetive" pra investigar perdas sem pular entre
 * 4 telas.
 *
 * props:
 * - orderId: id da ordem a investigar (null = modal fechado)
 * - onClose: callback ao fechar
 */
function PostMortemModal({ orderId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!orderId) return;
        setLoading(true);
        setData(null);
        getPostMortem(orderId)
            .then(r => { setData(r); setLoading(false); })
            .catch(() => setLoading(false));
    }, [orderId]);

    if (!orderId) return null;

    const o = data && data.order;
    const ai = data && data.aiSignalDetail;
    const rt = data && data.roundTrip;
    const pair = data && data.pairOrder;

    return (
        <div className='modal show d-block' tabIndex='-1' style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
            <div className='modal-dialog modal-dialog-centered modal-lg' onClick={(e) => e.stopPropagation()}>
                <div className='modal-content'>
                    <div className='modal-header'>
                        <h5 className='modal-title'>
                            🔍 Post-mortem
                            {o && <span className='ms-2 text-muted' style={{ fontSize: 14 }}>{o.symbol} · {o.side}</span>}
                        </h5>
                        <button type='button' className='btn-close' onClick={onClose} aria-label='Fechar' />
                    </div>
                    <div className='modal-body'>
                        {loading && <div className='text-muted text-center py-3'>Carregando contexto...</div>}
                        {!loading && !data && <div className='text-muted text-center py-3'>Sem dados retornados.</div>}
                        {!loading && data && (
                            <>
                                {/* Round-trip summary (se pareada) */}
                                {rt && (
                                    <div className={`card border-${rt.pnl >= 0 ? 'success' : 'danger'} mb-3`}>
                                        <div className='card-body py-2'>
                                            <div className='d-flex justify-content-between flex-wrap'>
                                                <strong style={{ fontSize: 14 }}>
                                                    Round-trip: ${formatNumber(rt.buyPrice)} → ${formatNumber(rt.sellPrice)}
                                                </strong>
                                                <span className={`fw-bold ${rt.pnl >= 0 ? 'text-success' : 'text-danger'}`}>
                                                    {rt.pnl >= 0 ? '+' : ''}{rt.pnl.toFixed(4)} ({rt.pnlPct >= 0 ? '+' : ''}{rt.pnlPct}%)
                                                </span>
                                            </div>
                                            <small className='text-muted'>Duração: {fmtDuration(rt.durationMs)} · Quantidade: {formatNumber(rt.quantity)}</small>
                                        </div>
                                    </div>
                                )}

                                {/* Ordem */}
                                <div className='card mb-3'>
                                    <div className='card-header py-2'><strong style={{ fontSize: 13 }}>📋 Ordem</strong></div>
                                    <div className='card-body py-2' style={{ fontSize: 13 }}>
                                        <Row label='Data' value={fmtDate(o.transactTime)} />
                                        <Row label='Side' value={<span className={`badge bg-${o.side === 'BUY' ? 'success' : 'danger'}`}>{o.side}</span>} />
                                        <Row label='Tipo' value={o.type} />
                                        <Row label='Status' value={o.status} />
                                        <Row label='Preço médio' value={o.avgPrice ? `$${formatNumber(parseFloat(o.avgPrice))}` : '—'} />
                                        <Row label='Quantidade' value={formatNumber(parseFloat(o.quantity))} />
                                        <Row label='Stop' value={o.stopPrice ? `$${formatNumber(parseFloat(o.stopPrice))}` : '—'} />
                                        <Row label='Net' value={o.net ? `$${formatNumber(parseFloat(o.net))}` : '—'} />
                                        {o.pnlPct != null && (
                                            <Row label='P&L %' value={
                                                <span className={parseFloat(o.pnlPct) >= 0 ? 'text-success' : 'text-danger'}>
                                                    {parseFloat(o.pnlPct) >= 0 ? '+' : ''}{parseFloat(o.pnlPct).toFixed(2)}%
                                                </span>
                                            } />
                                        )}
                                        <Row label='Ambiente' value={<span className={`badge ${o.environment === 'test' ? 'bg-warning text-dark' : 'bg-success'}`}>{o.environment || '—'}</span>} />
                                        {o.exchange && <Row label='Corretora' value={o.exchange} />}
                                    </div>
                                </div>

                                {/* Contexto IA */}
                                {(o.aiSignal || ai) && (
                                    <div className='card mb-3'>
                                        <div className='card-header py-2'><strong style={{ fontSize: 13 }}>🤖 Contexto da IA</strong></div>
                                        <div className='card-body py-2' style={{ fontSize: 13 }}>
                                            <Row label='Sinal' value={
                                                <span className={`badge bg-${o.aiSignal === 'BUY' ? 'success' : o.aiSignal === 'SELL' ? 'danger' : 'secondary'}`}>
                                                    {o.aiSignal || (ai && ai.signal) || '—'}
                                                </span>
                                            } />
                                            {o.aiConfidence != null && (
                                                <Row label='Confiança' value={`${Math.round(parseFloat(o.aiConfidence) * 100)}%`} />
                                            )}
                                            {o.aiProvider && <Row label='Provedor' value={`${o.aiProvider}${o.aiModel ? ` (${o.aiModel})` : ''}`} />}
                                            {ai && ai.reasoning && (
                                                <Row label='Raciocínio' value={<em className='text-muted'>"{ai.reasoning}"</em>} />
                                            )}
                                            {ai && ai.stopLoss && <Row label='Stop sugerido' value={`$${formatNumber(parseFloat(ai.stopLoss))}`} />}
                                            {ai && ai.takeProfit && <Row label='Alvo sugerido' value={`$${formatNumber(parseFloat(ai.takeProfit))}`} />}
                                            {ai && ai.outcome && (
                                                <Row label='Outcome do sinal' value={
                                                    <span className={`badge ${ai.outcome === 'CORRECT' ? 'bg-success' : ai.outcome === 'INCORRECT' ? 'bg-danger' : 'bg-secondary'}`}>
                                                        {ai.outcome}
                                                    </span>
                                                } />
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Regime de mercado */}
                                {o.regime && (
                                    <div className='card mb-3'>
                                        <div className='card-header py-2'><strong style={{ fontSize: 13 }}>🌡️ Regime de Mercado</strong></div>
                                        <div className='card-body py-2' style={{ fontSize: 13 }}>
                                            <Row label='Regime na hora' value={REGIME_LABELS[o.regime] || o.regime} />
                                        </div>
                                    </div>
                                )}

                                {/* Automação */}
                                {o.automation && (
                                    <div className='card mb-3'>
                                        <div className='card-header py-2'><strong style={{ fontSize: 13 }}>⚙️ Automação que disparou</strong></div>
                                        <div className='card-body py-2' style={{ fontSize: 13 }}>
                                            <Row label='Nome' value={o.automation.name} />
                                            {o.automation.schedule && <Row label='Schedule' value={<code style={{ fontSize: 11 }}>{o.automation.schedule}</code>} />}
                                            {o.automation.conditions && (
                                                <Row label='Condição' value={
                                                    <code style={{ fontSize: 10, wordBreak: 'break-all' }}>{o.automation.conditions}</code>
                                                } />
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Trade pareada */}
                                {pair && (
                                    <div className='card mb-2 border-info'>
                                        <div className='card-header py-2'>
                                            <strong style={{ fontSize: 13 }}>🔗 Trade pareada (round-trip)</strong>
                                        </div>
                                        <div className='card-body py-2' style={{ fontSize: 13 }}>
                                            <Row label='Side' value={<span className={`badge bg-${pair.side === 'BUY' ? 'success' : 'danger'}`}>{pair.side}</span>} />
                                            <Row label='Data' value={fmtDate(pair.transactTime)} />
                                            <Row label='Preço' value={pair.avgPrice ? `$${formatNumber(parseFloat(pair.avgPrice))}` : '—'} />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className='modal-footer'>
                        <button type='button' className='btn btn-sm btn-secondary' onClick={onClose}>Fechar</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className='d-flex justify-content-between align-items-start mb-1'>
            <span className='text-muted me-3' style={{ minWidth: 110 }}>{label}:</span>
            <span className='text-end' style={{ wordBreak: 'break-word' }}>{value}</span>
        </div>
    );
}

export default PostMortemModal;
